
// Service to fetch local weather and generate comprehension drills

const WEATHER_CODES = {
    0: 'CLEAR SKIES',
    1: 'MAINLY CLEAR',
    2: 'PARTLY CLOUDY',
    3: 'OVERCAST',
    45: 'FOG',
    48: 'DEPOSITING RIME FOG',
    51: 'LIGHT DRIZZLE',
    53: 'MODERATE DRIZZLE',
    55: 'DENSE DRIZZLE',
    61: 'SLIGHT RAIN',
    63: 'MODERATE RAIN',
    65: 'HEAVY RAIN',
    71: 'SLIGHT SNOW',
    73: 'MODERATE SNOW',
    75: 'HEAVY SNOW',
    95: 'THUNDERSTORM',
};

const getCardinalDirection = (angle) => {
    const directions = ['NORTH', 'NORTH EAST', 'EAST', 'SOUTH EAST', 'SOUTH', 'SOUTH WEST', 'WEST', 'NORTH WEST'];
    return directions[Math.round(angle / 45) % 8];
};

// Capital cities mapping (US States & CA Provinces)
const CAPITALS = {
    // US States
    'AL': { lat: 32.3617, lon: -86.2792 }, // Montgomery
    'AK': { lat: 58.3019, lon: -134.4197 }, // Juneau
    'AZ': { lat: 33.4484, lon: -112.0740 }, // Phoenix
    'AR': { lat: 34.7360, lon: -92.3311 }, // Little Rock
    'CA': { lat: 38.5816, lon: -121.4944 }, // Sacramento
    'CO': { lat: 39.7392, lon: -104.9903 }, // Denver
    'CT': { lat: 41.7665, lon: -72.6732 }, // Hartford
    'DE': { lat: 39.1580, lon: -75.5244 }, // Dover
    'FL': { lat: 30.4380, lon: -84.2806 }, // Tallahassee
    'GA': { lat: 33.7629, lon: -84.4227 }, // Atlanta
    'HI': { lat: 21.3045, lon: -157.8557 }, // Honolulu
    'ID': { lat: 43.6137, lon: -116.1998 }, // Boise
    'IL': { lat: 39.7833, lon: -89.6503 }, // Springfield
    'IN': { lat: 39.7683, lon: -86.1584 }, // Indianapolis
    'IA': { lat: 41.5909, lon: -93.6208 }, // Des Moines
    'KS': { lat: 39.04, lon: -95.69 }, // Topeka
    'KY': { lat: 38.1867, lon: -84.8753 }, // Frankfort
    'LA': { lat: 30.4507, lon: -91.1391 }, // Baton Rouge
    'ME': { lat: 44.3235, lon: -69.7653 }, // Augusta
    'MD': { lat: 38.9729, lon: -76.5012 }, // Annapolis
    'MA': { lat: 42.3580, lon: -71.0636 }, // Boston
    'MI': { lat: 42.7335, lon: -84.5467 }, // Lansing
    'MN': { lat: 44.95, lon: -93.094 }, // St. Paul
    'MS': { lat: 32.3205, lon: -90.2078 }, // Jackson
    'MO': { lat: 38.5729, lon: -92.1892 }, // Jefferson City
    'MT': { lat: 46.5937, lon: -112.0202 }, // Helena
    'NE': { lat: 40.8099, lon: -96.6788 }, // Lincoln
    'NV': { lat: 39.1609, lon: -119.7538 }, // Carson City
    'NH': { lat: 43.2069, lon: -71.5365 }, // Concord
    'NJ': { lat: 40.2217, lon: -74.7561 }, // Trenton
    'NM': { lat: 35.6672, lon: -105.9645 }, // Santa Fe
    'NY': { lat: 42.6526, lon: -73.7562 }, // Albany
    'NC': { lat: 35.7716, lon: -78.6353 }, // Raleigh
    'ND': { lat: 46.8133, lon: -100.7790 }, // Bismarck
    'OH': { lat: 39.9622, lon: -83.0006 }, // Columbus
    'OK': { lat: 35.4823, lon: -97.5350 }, // Oklahoma City
    'OR': { lat: 44.9311, lon: -123.0291 }, // Salem
    'PA': { lat: 40.2698, lon: -76.8756 }, // Harrisburg
    'RI': { lat: 41.8236, lon: -71.4222 }, // Providence
    'SC': { lat: 33.9973, lon: -81.0291 }, // Columbia
    'SD': { lat: 44.3680, lon: -100.3363 }, // Pierre
    'TN': { lat: 36.165, lon: -86.784 }, // Nashville
    'TX': { lat: 30.2666, lon: -97.75 }, // Austin
    'UT': { lat: 40.7547, lon: -111.8926 }, // Salt Lake City
    'VT': { lat: 44.2599, lon: -72.5714 }, // Montpelier
    'VA': { lat: 37.5385, lon: -77.4343 }, // Richmond
    'WA': { lat: 47.0425, lon: -122.8930 }, // Olympia
    'WV': { lat: 38.3472, lon: -81.6333 }, // Charleston
    'WI': { lat: 43.0747, lon: -89.3844 }, // Madison
    'WY': { lat: 41.1456, lon: -104.8020 }, // Cheyenne
    // Canadian Provinces
    'AB': { lat: 53.5354, lon: -113.5079 }, // Edmonton
    'BC': { lat: 48.4265, lon: -123.3640 }, // Victoria
    'MB': { lat: 49.8844, lon: -97.1470 }, // Winnipeg
    'NB': { lat: 45.9528, lon: -66.6404 }, // Fredericton
    'NL': { lat: 47.5670, lon: -52.7077 }, // St. John's
    'NS': { lat: 44.6464, lon: -63.5729 }, // Halifax
    'ON': { lat: 43.6532, lon: -79.3832 }, // Toronto
    'PE': { lat: 46.2403, lon: -63.1347 }, // Charlottetown
    'QC': { lat: 46.8123, lon: -71.2145 }, // Quebec City
    'SK': { lat: 50.4481, lon: -104.6189 }, // Regina
};

const MOCK_CONDITIONS = ['CLEAR', 'PARTLY CLOUDY', 'OVERCAST', 'RAIN', 'SNOW', 'FOG'];
const MOCK_DIRECTIONS = ['NORTH', 'SOUTH', 'EAST', 'WEST', 'NORTH EAST', 'NORTH WEST', 'SOUTH EAST', 'SOUTH WEST'];

const generateMockWeather = () => {
    const temp = Math.floor(Math.random() * (90 - 30) + 30); // 30-90 F
    const windSpd = Math.floor(Math.random() * 20);
    const windDir = MOCK_DIRECTIONS[Math.floor(Math.random() * MOCK_DIRECTIONS.length)];
    const condition = MOCK_CONDITIONS[Math.floor(Math.random() * MOCK_CONDITIONS.length)];

    return { temp, windSpd, windDir, condition, isMock: true };
};

export const fetchLocalWeatherDrill = async (userRegion = null) => {
    const generateDrillFromData = (data) => {
        const { temp, windSpd, windDir, condition } = data;
        // Text: LOCAL WX. TEMP 72 F. WIND NORTH 10 KNOTS. SKIES CLEAR.
        const text = `LOCAL WX. TEMP ${temp} F. WIND ${windDir} ${windSpd} KNOTS. SKIES ${condition}.`;

        const qType = Math.floor(Math.random() * 3);
        let question, answer, options;

        if (qType === 0) {
            question = 'What is the temperature?';
            answer = `${temp} F`;
            options = [`${temp} F`, `${temp - 10} F`, `${temp + 5} F`, `${temp + 10} F`].sort(() => Math.random() - 0.5);
        } else if (qType === 1) {
            question = 'What is the wind speed?';
            answer = `${windSpd} KNOTS`;
            options = [`${windSpd} KNOTS`, `${Math.max(0, windSpd - 5)} KNOTS`, `${windSpd + 5} KNOTS`, `${windSpd + 10} KNOTS`].sort(() => Math.random() - 0.5);
        } else {
            question = 'What is the wind direction?';
            answer = windDir;
            const allDirs = ['NORTH', 'SOUTH', 'EAST', 'WEST'];
            options = [windDir, ...allDirs.filter(d => d !== windDir).slice(0, 3)].sort(() => Math.random() - 0.5);
        }

        return {
            id: `wx_local_${Date.now()}`,
            text: text.toUpperCase(),
            question,
            options,
            answer,
            difficulty: 'Medium'
        };
    };

    const fetchWeatherFromAPI = async (lat, lon) => {
        const response = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,wind_speed_10m,wind_direction_10m,weather_code&temperature_unit=fahrenheit&wind_speed_unit=kn`
        );
        if (!response.ok) throw new Error('Weather API failed');
        const data = await response.json();
        const current = data.current;
        return {
            temp: Math.round(current.temperature_2m),
            windSpd: Math.round(current.wind_speed_10m),
            windDir: getCardinalDirection(current.wind_direction_10m),
            condition: WEATHER_CODES[current.weather_code] || 'CLOUDY'
        };
    };

    // Strategy 1: Browser Geolocation
    if (navigator.geolocation) {
        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 3000 });
            });
            const data = await fetchWeatherFromAPI(position.coords.latitude, position.coords.longitude);
            return generateDrillFromData(data);
        } catch (e) {
            console.warn('Geolocation failed, falling back to Region...', e);
        }
    }

    // Strategy 2: User Region Capital
    if (userRegion && CAPITALS[userRegion]) {
        try {
            const { lat, lon } = CAPITALS[userRegion];
            console.log(`Fetching weather for capital of ${userRegion}`);
            const data = await fetchWeatherFromAPI(lat, lon);
            return generateDrillFromData(data);
        } catch (e) {
            console.warn('Region weather fetch failed, falling back to mock...', e);
        }
    }

    // Strategy 3: Mock Data (Final Fallback)
    return generateDrillFromData(generateMockWeather());
};
